import SlideFrame from "@/components/SlideFrame";
import { Layers, FileText, Users, Sparkles, Scale } from "lucide-react";
import { isLegalPrimary, packFor } from "@/lib/industryPack";

/* ============================ 03 · Your Guide ============================ */
export function S_Guide({ idx, total, prospect }) {
  const pack = packFor(prospect);
  const corp = pack.corporate;
  const lf = pack.lawFirm;
  const legalPrimary = isLegalPrimary(prospect);
  const showCustom = corp || lf;
  const defaultCards = [
    { title: "Compliance experts",   body: "Senior compliance officers, attorneys, BSA/AML specialists — doing the actual work." },
    { title: "Scalable systems",     body: "Every workflow we build for you stays — yours to run after we hand off." },
    { title: "Evidence, not memos",  body: "No more Excel-and-PDF compliance. Audit-ready trails captured automatically." },
    { title: "Your compliance story", body: "You can tell partners + regulators what you do — with the proof to back it up." },
  ];
  const legalPrimaryCards = [
    { title: "Transparent attorneys",       body: "Your matter file is yours — every email, draft, and meeting note is searchable for you and instantly downloadable. No 'I'll get back to you tomorrow' on what we've worked on." },
    { title: "Collaborative, not transactional", body: "Work alongside us in real time — comment, redline, ask questions and see progress in hours, not weeks. No more 'wait for the call.'" },
    { title: "Tech that makes you look great", body: "When the C-suite asks 'what's legal doing this quarter?' you have receipts — clean reports, faster turnarounds, AI-tagged work product. Your department looks leveraged, modern, on top of it." },
    { title: "Optional compliance platform", body: "If your in-house GC + compliance team want the same tools we use — matter tracker, CompliMind, hour markers — bundle it in. Or pass." },
  ];
  const cards = legalPrimary
    ? legalPrimaryCards
    : (showCustom && pack.guideCards ? pack.guideCards : defaultCards);
  const pillIcons = legalPrimary
    ? [Scale, Users, Sparkles, Layers]
    : [Users, Layers, FileText, Sparkles];

  return (
    <SlideFrame idx={idx} total={total} kicker="Your Guide" dark>
      <div className="grid grid-cols-12 gap-10 pt-2 md:pt-4">
        <div className="col-span-12 md:col-span-7">
          <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-5">
            {legalPrimary ? "/ your outside counsel" : "/ who's behind the platform"}
          </p>
          <h2 className="serif font-light text-5xl md:text-6xl leading-[1.04] tracking-tight">
            {legalPrimary ? (
              <>Meet <em className="italic text-sun-600">CompliSun Legal PLC.</em></>
            ) : (
              <>Meet <em className="italic text-sun-600">CompliSun.</em></>
            )}
          </h2>
          {legalPrimary ? (
            <>
              <p className="mt-5 text-ink-900/80 text-lg leading-relaxed max-w-2xl">
                A modern law firm built for the way growing companies actually need legal help —
                ongoing access, evidence captured as we go, and tiered pricing so you only pay for
                the level of support you need this quarter.
              </p>
              <p className="mt-4 text-ink-900/75 text-base leading-relaxed max-w-2xl">
                Beyond outside counsel, your in-house GC and compliance team can plug into the
                same platform we use — matter status tracker, CompliMind, hour-budget guardrails.
                <span className="text-ink-900/55 italic"> Available as a layer, not required.</span>
              </p>
            </>
          ) : showCustom ? (
            <>
              <p className="mt-5 text-ink-900/80 text-lg leading-relaxed max-w-2xl">
                {pack.guidePromise}
              </p>
              {pack.guidePromise2 && (
                <p className="mt-4 text-ink-900/75 text-base leading-relaxed max-w-2xl">
                  {pack.guidePromise2}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="mt-5 text-ink-900/80 text-lg leading-relaxed max-w-2xl">
                We're the compliance team you'd hire if you could afford 12 people instead of one
                — paired with automation that handles the mundane work so your team can spend their
                hours on the things that actually need a human brain.
              </p>
              <p className="mt-4 text-ink-900/75 text-base leading-relaxed max-w-2xl">
                {pack.guidePromise}
              </p>
            </>
          )}

          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            {cards.map((c, i) => (
              <Pill key={c.title} icon={pillIcons[i] || Users} title={c.title} body={c.body} />
            ))}
          </div>
        </div>

        {/* Colin as PART of the team, not the protagonist */}
        <div className="col-span-12 md:col-span-5">
          <div className="border border-ink-900/12 rounded-md bg-white/75 p-5" data-testid="guide-colin-card">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700">/ {pack.colinTitle || "founder · compliance lead"}</div>
            <div className="flex items-start gap-4 mt-3">
              <img
                src="https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/v9nyy5uz_colin.png"
                alt="Colin Darke"
                className="h-16 w-16 rounded-full object-cover border border-ink-900/15 headshot"
              />
              <div>
                <div className="serif text-lg text-ink-900 leading-tight">Colin Darke</div>
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mt-0.5">CEO · CompliSun</div>
                <p className="mt-2 text-sm text-ink-900/75 leading-relaxed">
                  {showCustom && pack.colinBio
                    ? pack.colinBio
                    : "Former General Counsel and Chief Compliance Officer for Rocket Loans. Advises banks and fintechs. Built CompliSun so the compliance work he used to do by hand could ship as software AND a team — not a folder of Word docs."}
                </p>
              </div>
            </div>
          </div>

          <p className="mt-3 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 text-center">
            {showCustom ? "/ full team profiled on the Team slide" : "/ 9-person fractional team profiled on the Team slide"}
          </p>
        </div>
      </div>
    </SlideFrame>
  );
}

function Pill({ icon: Icon, title, body }) {
  return (
    <div className="border border-ink-900/10 rounded-md p-3.5 bg-white/65 hover:bg-white transition-colors">
      <Icon className="h-4 w-4 text-sun-700 mb-1.5" strokeWidth={1.5} />
      <div className="serif text-base text-ink-900 leading-tight">{title}</div>
      <div className="text-xs text-ink-900/65 mt-1 leading-relaxed">{body}</div>
    </div>
  );
}
